import { useEffect, useState, type FormEvent } from 'react';
import { useAuth } from '@/app/providers/AuthProvider';
import { exhibitorsApi } from '@/shared/api/exhibitorsApi';
import { httpClient } from '@/shared/api/httpClient';
import { endpoints } from '@/shared/api/endpoints';
import { leadsApi } from '@/features/leads/api/leadsApi';
import { ResetPasswordForm } from '@/features/admin/users/components/ResetPasswordForm';
import { Button } from '@/shared/components/Button';
import { ExportButton } from '@/shared/components/ExportButton';
import { Spinner } from '@/shared/components/Spinner';
import { EmptyState } from '@/shared/components/EmptyState';
import { Pill } from '@/shared/components/Pill';
import { ApiError } from '@/shared/api/httpClient';
import type { ExhibitorDto, TeamMemberStatsDto, UserDto } from '@/shared/types/domain';
import './TeamPage.css';

const teamApi = {
  listStaff: (exhibitorId: string) => httpClient.get<UserDto[]>(endpoints.users.byExhibitor(exhibitorId)),
  createStaff: (data: { email: string; firstName: string; lastName: string; password: string; role: 'ExhibitorAdmin' | 'ExhibitorStaff'; exhibitorId: string }) =>
    httpClient.post<UserDto>(endpoints.users.create, data),
  updateStaff: (id: string, data: { firstName: string; lastName: string; role: 'ExhibitorAdmin' | 'ExhibitorStaff'; isActive: boolean }) =>
    httpClient.put<UserDto>(endpoints.users.update(id), data),
  deleteStaff: (id: string) => httpClient.delete<void>(endpoints.users.delete(id)),
  scanStats: (exhibitorId: string) => httpClient.get<TeamMemberStatsDto[]>(endpoints.users.scanStats(exhibitorId))
};

export function TeamPage() {
  const { user } = useAuth();
  const exhibitorId = user?.exhibitorId;

  const [exhibitor, setExhibitor] = useState<ExhibitorDto | null>(null);
  const [staff, setStaff] = useState<UserDto[]>([]);
  const [stats, setStats] = useState<TeamMemberStatsDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [resettingId, setResettingId] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  function refetch() {
    if (!exhibitorId) return;
    setIsLoading(true);
    Promise.all([exhibitorsApi.getById(exhibitorId), teamApi.listStaff(exhibitorId), teamApi.scanStats(exhibitorId)])
      .then(([ex, s, st]) => {
        setExhibitor(ex);
        setStaff(s);
        setStats(st);
      })
      .finally(() => setIsLoading(false));
  }

  useEffect(refetch, [exhibitorId]);

  async function handleDelete(userId: string) {
    if (!window.confirm('Remove this team member?')) return;
    await teamApi.deleteStaff(userId);
    refetch();
  }

    async function handleExportTeam(format: 'csv' | 'xlsx') {
    if (!exhibitorId) return;
    setExportError(null);
    try {
      await (format === 'csv' ? leadsApi.exportTeamCsv(exhibitorId) : leadsApi.exportTeamXlsx(exhibitorId));
    } catch (err) {
      setExportError(err instanceof ApiError ? err.userMessage : `Could not export ${format.toUpperCase()}.`);
    }
  }

  if (!exhibitorId) {
    return <EmptyState icon="◎" title="No exhibitor on your account" />;
  }

  if (isLoading || !exhibitor) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-7)' }}>
        <Spinner label="Loading team" />
      </div>
    );
  }

  const seatsUsed = exhibitor.staffCount;
  const seatsMax = exhibitor.maxStaffSeats;
  const atLimit = seatsUsed >= seatsMax;

  return (
    <div>
      <h1 className="font-display" style={{ fontSize: '1.375rem', marginBottom: 'var(--space-4)' }}>
        Team
      </h1>

      <div className="team-page__seats">
        <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
          {seatsUsed} of {seatsMax} seats
        </span>
        <div className="team-page__seats-bar">
          <div
            className="team-page__seats-fill"
            style={{ width: `${Math.min(100, (seatsUsed / seatsMax) * 100)}%` }}
          />
        </div>
      </div>

      <Button onClick={() => setShowCreate((v) => !v)} disabled={atLimit && !showCreate} fullWidth style={{ marginBottom: 'var(--space-4)' }}>
        {showCreate ? 'Cancel' : atLimit ? 'Seat limit reached' : '+ Add team member'}
      </Button>

      {atLimit && !showCreate && (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', textAlign: 'center', marginTop: -8, marginBottom: 'var(--space-4)' }}>
          Deactivate someone below to free up a seat, or contact us to increase your limit.
        </p>
      )}

      {showCreate && (
        <CreateStaffForm
          exhibitorId={exhibitorId}
          onCreated={() => {
            setShowCreate(false);
            refetch();
          }}
        />
      )}

      {staff.map((member) => (
        <div key={member.id} className="team-page__member">
          <div className="team-page__member-header">
            <div className="team-page__member-name">
              <div style={{ fontWeight: 600 }}>
                {member.firstName} {member.lastName}
                {member.id === user?.userId && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> (you)</span>}
              </div>
              <div className="team-page__member-email">{member.email}</div>
            </div>
            <Pill tone={member.isActive ? 'online' : 'offline'}>{member.isActive ? 'Active' : 'Disabled'}</Pill>
          </div>

          <div className="team-page__member-actions">
            <button
              type="button"
              onClick={() => {
                setEditingId(editingId === member.id ? null : member.id);
                setResettingId(null);
              }}
            >
              {editingId === member.id ? 'Close' : 'Edit'}
            </button>
            <button
              type="button"
              onClick={() => {
                setResettingId(resettingId === member.id ? null : member.id);
                setEditingId(null);
              }}
            >
              {resettingId === member.id ? 'Close' : 'Reset password'}
            </button>
            <button type="button" className="danger" onClick={() => void handleDelete(member.id)}>
              Remove
            </button>
          </div>

          {editingId === member.id && (
            <div style={{ marginTop: 'var(--space-3)' }}>
              <EditStaffForm
                user={member}
                onSaved={() => {
                  setEditingId(null);
                  refetch();
                }}
              />
            </div>
          )}

          {resettingId === member.id && (
            <div style={{ marginTop: 'var(--space-3)' }}>
              <ResetPasswordForm userId={member.id} onDone={() => setResettingId(null)} />
            </div>
          )}
        </div>
      ))}

      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--space-4)',
          margin: 'var(--space-6) 0 var(--space-4)'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-3)' }}>
          <div>
            <div className="stat-card__value" style={{ fontSize: '1.5rem' }}>{exhibitor.scanCount}</div>
            <div className="stat-card__label">Total scans (whole team)</div>
          </div>
        </div>
        {exportError && <div className="error-banner" style={{ marginBottom: 'var(--space-3)' }}>{exportError}</div>}
        <ExportButton onExport={handleExportTeam} />
      </div>

      {stats.length > 0 && (
        <>
          <h2 className="font-display" style={{ fontSize: '1.125rem', marginBottom: 'var(--space-3)' }}>
            Scans by team member
          </h2>
          {stats.map((s, i) => (
            <div key={s.userId} className="team-page__leaderboard-row">
              <span>
                {i + 1}. {s.firstName} {s.lastName}
              </span>
              <span className="font-mono" style={{ color: 'var(--text-muted)' }}>
                {s.scanCount}
              </span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function CreateStaffForm({ exhibitorId, onCreated }: { exhibitorId: string; onCreated: () => void }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'ExhibitorAdmin' | 'ExhibitorStaff'>('ExhibitorStaff');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setIsSaving(true);

    try {
      await teamApi.createStaff({ email, firstName, lastName, password, role, exhibitorId });
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.userMessage : 'Could not add team member.');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-4)',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-5)',
        marginBottom: 'var(--space-4)'
      }}
    >
      {error && <div className="error-banner">{error}</div>}
      <div className="field">
        <label className="field__label">First name</label>
        <input className="field__input" required value={firstName} onChange={(e) => setFirstName(e.target.value)} />
      </div>
      <div className="field">
        <label className="field__label">Last name</label>
        <input className="field__input" required value={lastName} onChange={(e) => setLastName(e.target.value)} />
      </div>
      <div className="field">
        <label className="field__label">Email</label>
        <input type="email" className="field__input" required value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div className="field">
        <label className="field__label">Temporary password</label>
        <input
          type="text"
          className="field__input"
          required
          minLength={10}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      <div className="field">
        <label className="field__label">Role</label>
        <select className="field__input" value={role} onChange={(e) => setRole(e.target.value as typeof role)}>
          <option value="ExhibitorStaff">Staff</option>
          <option value="ExhibitorAdmin">Admin</option>
        </select>
      </div>
      <Button type="submit" isLoading={isSaving} fullWidth>
        Add to team
      </Button>
    </form>
  );
}

function EditStaffForm({ user, onSaved }: { user: UserDto; onSaved: () => void }) {
  const [firstName, setFirstName] = useState(user.firstName);
  const [lastName, setLastName] = useState(user.lastName);
  const [role, setRole] = useState(user.role as 'ExhibitorAdmin' | 'ExhibitorStaff');
  const [isActive, setIsActive] = useState(user.isActive);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setIsSaving(true);

    try {
      await teamApi.updateStaff(user.id, { firstName, lastName, role, isActive });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.userMessage : 'Could not save changes.');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {error && <div className="error-banner">{error}</div>}
      <div className="field">
        <label className="field__label">First name</label>
        <input className="field__input" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
      </div>
      <div className="field">
        <label className="field__label">Last name</label>
        <input className="field__input" value={lastName} onChange={(e) => setLastName(e.target.value)} />
      </div>
      <div className="field">
        <label className="field__label">Role</label>
        <select className="field__input" value={role} onChange={(e) => setRole(e.target.value as typeof role)}>
          <option value="ExhibitorStaff">Staff</option>
          <option value="ExhibitorAdmin">Admin</option>
        </select>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
        Active
      </label>
      <Button type="submit" isLoading={isSaving} fullWidth>
        Save
      </Button>
    </form>
  );
}